import type { EntryKey } from "@pinia/colada";
import type { VoiceParams } from "@/api/tts.api";
import { defineQuery, useQuery, useQueryCache } from "@pinia/colada";
import { api } from "@/services/api";
import { hasPermission } from "@/stores/user-profile.store";

/**
 * Query keys for TTS Voices
 */
export const TTS_VOICES_QUERY_KEYS = {
  root: ["tts", "voices"] as const,
};

/**
 * Query to fetch all available TTS voices grouped by model
 *
 * @returns Record<string, VoiceParams[]> - Voices grouped by model name
 *
 * @example
 * const voicesQuery = useTTSVoicesQuery();
 * const azureVoices = voicesQuery.data.value?.["azure-tts"] ?? [];
 */
export const useTTSVoicesQuery = defineQuery(() => {
  return useQuery({
    key: TTS_VOICES_QUERY_KEYS.root as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<Record<string, VoiceParams[]>>("/tts/voices");
      return data;
    },
    enabled: () => hasPermission("tts:execute"),
    staleTime: 1000 * 60 * 5, // 5 minutes - voices rarely change
    gcTime: 1000 * 60 * 15, // 15 minutes
  });
});

/**
 * Helper to invalidate TTS voices cache
 */
export function useInvalidateTTSVoices() {
  const queryCache = useQueryCache();
  return () => {
    queryCache.invalidateQueries({ key: TTS_VOICES_QUERY_KEYS.root as unknown as EntryKey });
  };
}
